import type { MessageReference, PendingMessage } from '@liniya/api-client';
import { create } from 'zustand';

interface PendingState {
  /** Messages awaiting confirmation, keyed by chat then by client message id. */
  byChat: Record<string, Record<string, PendingMessage>>;

  add: (message: PendingMessage) => void;
  update: (chatId: string, messageId: string, patch: Partial<PendingMessage>) => void;
  remove: (chatId: string, messageId: string) => void;
  list: (chatId: string) => PendingMessage[];
  get: (chatId: string, messageId: string) => PendingMessage | undefined;

  /**
   * Register how to abort an upload in progress.
   *
   * Kept outside the message itself: a cancel function is not state anyone
   * renders, and putting it in the store's data would make every subscriber
   * re-render when an upload starts.
   */
  registerCancel: (chatId: string, messageId: string, cancel: () => void) => void;
  /** True when there was something to cancel. */
  cancel: (chatId: string, messageId: string) => boolean;
}

/** Abort handles, by chat and message. Deliberately not part of the store's state. */
const cancellations = new Map<string, () => void>();

function cancelKey(chatId: string, messageId: string): string {
  return `${chatId}/${messageId}`;
}

/**
 * The waiting room between pressing Send and the server confirming.
 *
 * A message appears here the instant it is composed and leaves the moment the
 * confirmed copy shows up in the message cache, so the interface never has to
 * choose between showing nothing and showing something it cannot yet prove.
 * Because the client generates the message id and the server treats it as an
 * idempotency key, the pending entry and the eventual real message are the
 * same message — not a guess that has to be reconciled.
 */
export const usePendingStore = create<PendingState>((set, get) => ({
  byChat: {},

  add: (message) =>
    set((state) => ({
      byChat: {
        ...state.byChat,
        [message.chat_id]: {
          ...(state.byChat[message.chat_id] ?? {}),
          [message.message_id]: message,
        },
      },
    })),

  update: (chatId, messageId, patch) =>
    set((state) => {
      const chat = state.byChat[chatId];
      const existing = chat?.[messageId];
      if (!existing) return state;
      return {
        byChat: {
          ...state.byChat,
          [chatId]: { ...chat, [messageId]: { ...existing, ...patch } },
        },
      };
    }),

  remove: (chatId, messageId) => {
    cancellations.delete(cancelKey(chatId, messageId));
    set((state) => {
      const chat = state.byChat[chatId];
      if (!chat || !(messageId in chat)) return state;
      const { [messageId]: removed, ...rest } = chat;
      void removed;
      return { byChat: { ...state.byChat, [chatId]: rest } };
    });
  },

  registerCancel: (chatId, messageId, cancel) => {
    cancellations.set(cancelKey(chatId, messageId), cancel);
  },

  /**
   * Abort an upload and forget the message.
   *
   * A large photo on a slow uplink can take a minute, and until now there was
   * no way out of it but to close the app: the bubble sat at some percentage
   * with nothing to press. Cancelling aborts the request and removes the
   * message, because a half-uploaded attachment is not a message anyone wants
   * kept.
   */
  cancel: (chatId, messageId) => {
    const abort = cancellations.get(cancelKey(chatId, messageId));
    abort?.();
    get().remove(chatId, messageId);
    return abort !== undefined;
  },

  list: (chatId) => {
    const chat = get().byChat[chatId];
    if (!chat) return [];
    return Object.values(chat).sort((left, right) =>
      left.createdAt.localeCompare(right.createdAt),
    );
  },

  get: (chatId, messageId) => get().byChat[chatId]?.[messageId],
}));

export function createPendingMessage(input: {
  messageId: string;
  chatId: string;
  content: string;
  reference: MessageReference | null;
  attachmentName?: string;
  localPreviewUrl?: string;
}): PendingMessage {
  return {
    message_id: input.messageId,
    chat_id: input.chatId,
    content: input.content,
    createdAt: new Date().toISOString(),
    state: 'pending',
    reference: input.reference,
    ...(input.attachmentName ? { attachmentName: input.attachmentName } : {}),
    ...(input.localPreviewUrl ? { localPreviewUrl: input.localPreviewUrl } : {}),
  };
}
